import { displayDateString, isNotUndefined, KeyedCaseValueCalc } from "Backend";
import { Case, CaseDisplayGroup, CustomCaseProperty, CustomPropertyType } from "generated/graphql";
import { useMemo } from "react";
import { useAppSelector } from "redux/hooks";
import { selectCaseCalcs } from "redux/slices/case";
import {
  selectCasePropertySchemas,
  selectClientLensCaseInfoGroups,
  selectClientLensCaseDefaultGroup,
} from "redux/slices/clientLens";
import { CASE_DISPLAY_GROUP_DEFAULT_NAME } from "routes/lens-editor/custom-properties/case-display-groups.loader";
import { useGroupLocations } from "routes/lens-editor/custom-properties/useGroupLocations";

interface PropertyValue {
  caseProperty?: CustomCaseProperty;
  caseCalc?: KeyedCaseValueCalc;
  value: string | undefined;
}

export interface CaseDisplayGroupWithPropertyValues {
  group: CaseDisplayGroup;
  groupHidden: boolean;
  groupEmpty: boolean;
  propertyValues: PropertyValue[];
  rank: number;
}

export interface UseCaseInfoDisplayGroups {
  defaultGroup?: CaseDisplayGroupWithPropertyValues;
  visibleGroups: CaseDisplayGroupWithPropertyValues[];
  emptyGroups: CaseDisplayGroupWithPropertyValues[];
  hiddenGroups: CaseDisplayGroupWithPropertyValues[];
  allGroups: CaseDisplayGroupWithPropertyValues[];
}

export function useCaseInfoDisplayGroups(mycase: Case, calcValues: KeyedCaseValueCalc[]): UseCaseInfoDisplayGroups {
  const caseProperties = useAppSelector(selectCasePropertySchemas);
  const calcs = useAppSelector(selectCaseCalcs);
  const caseDisplayGroups = useAppSelector(selectClientLensCaseInfoGroups);
  const groupLocations = useGroupLocations(caseDisplayGroups).caseInfo;
  const defaultCaseDisplayGroup = useAppSelector(selectClientLensCaseDefaultGroup);
  const casePropertyValues: PropertyValue[] = useMemo(
    () =>
      caseProperties.map((cp) => {
        const value = mycase.properties.find((p) => p.key === cp.key)?.value;
        const formattedValue = cp.dataType === CustomPropertyType.Date ? displayDateString(value) || undefined : value;
        return {
          caseProperty: cp,
          value: formattedValue,
        };
      }),
    [caseProperties, mycase]
  );
  const caseCalcPropertyValues: PropertyValue[] = useMemo(
    () =>
      calcs.map((c) => {
        const value = calcValues.find((cv) => cv.key === c.key)?.value;
        const formattedValue = c.dataType === CustomPropertyType.Date ? displayDateString(value) || undefined : value;
        return {
          caseCalc: c,
          value: formattedValue,
        };
      }),
    [calcs, mycase]
  );
  const groupItems = casePropertyValues.concat(caseCalcPropertyValues);
  const caseDisplayGroupsWithPropertyValues: CaseDisplayGroupWithPropertyValues[] = useMemo(() => {
    return caseDisplayGroups.concat(defaultCaseDisplayGroup ? [defaultCaseDisplayGroup] : []).map((cdg) => {
      const propertyValues = cdg.items
        .map((item) =>
          groupItems.find((groupItem) =>
            item.caseCalcKey
              ? groupItem.caseCalc?.key === item.caseCalcKey
              : groupItem.caseProperty?.key === item.casePropertyKey
          )
        )
        .filter(isNotUndefined<PropertyValue>);
      return {
        group: cdg,
        propertyValues,
        groupEmpty: propertyValues.every((pv) => pv.value === undefined || pv.caseProperty?.isHidden),
        groupHidden: propertyValues.every((pv) => pv.caseProperty?.isHidden),
        rank: groupLocations.find((gl) => gl.group.id === cdg.id)?.location.rank ?? 0,
      };
    });
  }, [caseDisplayGroups, casePropertyValues, groupLocations]);

  let defaultGroup;
  const visibleGroups: CaseDisplayGroupWithPropertyValues[] = [];
  const emptyGroups: CaseDisplayGroupWithPropertyValues[] = [];
  const hiddenGroups: CaseDisplayGroupWithPropertyValues[] = [];
  const sortedGroups = caseDisplayGroupsWithPropertyValues.slice().sort((a, b) => a.rank - b.rank);

  sortedGroups.forEach((g) => {
    if (g.group.name === CASE_DISPLAY_GROUP_DEFAULT_NAME) {
      defaultGroup = g;
    } else if (!g.groupEmpty && !g.groupHidden) {
      visibleGroups.push(g);
    } else if (g.groupEmpty && !g.groupHidden) {
      emptyGroups.push(g);
    } else {
      hiddenGroups.push(g);
    }
  });

  return {
    defaultGroup,
    visibleGroups,
    emptyGroups,
    hiddenGroups,
    allGroups: sortedGroups,
  };
}
